import { Box, useColorModeValue } from '@chakra-ui/react'
import DOMPurify from 'dompurify'
import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { convertYoutubeUrlToEmbed, isYoutubeLink } from '../../core/parsers/markdownParser'
import { LinkChip } from '../LinkChip'

interface AnswerTextProps {
  htmlContent?: string
  children?: React.ReactNode
  markdownContent?: string
}

const sanitizeHtml = (html: string) => {
  // Configure DOMPurify with allowed tags and attributes
  const config = {
    ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'p', 'br', 'ul', 'ol', 'li', 'a', 'img', 'iframe'],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'src',
      'alt',
      'width',
      'height',
      'frameborder',
      'allowfullscreen',
      'allow',
    ],
    FORCE_REL: 'noopener noreferrer',
    FORCE_TARGET: '_blank',
  }

  return DOMPurify.sanitize(html, config)
}

const AnswerText: React.FC<AnswerTextProps> = ({ htmlContent, children, markdownContent }) => {
  // Color mode values
  const textColor = useColorModeValue('gray.900', 'gray.100')
  const codeBg = useColorModeValue('gray.50', 'gray.700')
  const cardBg = useColorModeValue('rgb(249, 249, 249)', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const linkColor = useColorModeValue('blue.500', 'blue.300')
  const linkHoverColor = useColorModeValue('blue.600', 'blue.400')

  return (
    <>
      {htmlContent ? (
        <Box
          pr={2}
          borderRadius='0px 16px 16px 0px'
          color={textColor}
          fontSize='15px'
          fontWeight='400'
          lineHeight='1.2em'
          whiteSpace='pre-line'
          minWidth={['60%', '60%', '50%']}
          maxWidth={['90%', '90%', '85%']}
          display='flex'
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlContent) }}
          sx={{
            '& table': {
              borderCollapse: 'collapse',
            },
            '& td, & th': {
              border: '1px solid',
              padding: '5px',
            },
            '& ul': {
              marginLeft: '20px',
            },
          }}
        />
      ) : markdownContent ? (
        <Box
          pr={2}
          borderRadius='0px 16px 16px 0px'
          color={textColor}
          fontSize='15px'
          fontWeight='400'
          lineHeight='1.2em'
          minWidth={['60%', '60%', '50%']}
          maxWidth={['90%', '90%', '85%']}
          display='flex'
          flexDirection='column'
          sx={{
            '& pre': {
              bg: codeBg,
              p: 4,
              borderRadius: 'md',
              overflowX: 'auto',
            },
            '& code': {
              fontFamily: 'mono',
              fontSize: '14px',
            },
            '& p': {
              my: 2,
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              fontWeight: 'bold',
              my: 3,
            },
            '& ul, & ol': {
              ml: 6,
              my: 2,
            },
            '& li': {
              my: 2,
            },
            '& hr': {
              borderColor: textColor,
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
            },
            '& td, & th': {
              border: '1px solid',
              padding: '8px',
            },
            '& blockquote': {
              borderLeft: '4px solid',
              borderColor: borderColor,
              pl: 4,
              my: 2,
            },
            '& a': {
              color: linkColor,
              textDecoration: 'underline',
              cursor: 'pointer',
              _hover: {
                color: linkHoverColor,
              },
            },
            '& iframe': {
              my: 2,
              maxWidth: '100%',
              border: 'none',
              borderRadius: 'md',
            },
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ children, ...props }: React.ComponentProps<'a'>) => {
                const href = props.href || ''

                // Handle YouTube links
                if (isYoutubeLink(href)) {
                  // Use dangerouslySetInnerHTML to render the iframe
                  return (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: convertYoutubeUrlToEmbed(href),
                      }}
                    />
                  )
                }

                const linkText = React.Children.toArray(children)
                  .map(c => (typeof c === 'string' ? c : ''))
                  .join('')
                  .trim()

                // Use LinkChip for all external links (including SharePoint)
                return <LinkChip href={href} label={linkText || href} title={linkText || href} />
              },
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </Box>
      ) : (
        <Box
          p={8}
          py={4}
          background={cardBg}
          borderRadius={8}
          boxShadow='0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
          fontSize='15px'
          fontWeight='400'
          lineHeight='22px'
          whiteSpace='pre-line'
          maxWidth='60%'
          display='flex'
          flexDirection='column'
          minWidth='500px'
          sx={{
            '& table': {
              borderCollapse: 'collapse',
            },
            '& td, & th': {
              border: '1px solid',
              padding: '5px',
            },
            '> p': {
              margin: 0,
            },
          }}
        >
          {children}
        </Box>
      )}
    </>
  )
}

export default React.memo(AnswerText)
